/* Mirror the drawer's open state to the root so late-loaded Shopify Inbox
   components do not overlay the checkout CTA. No external widget API needed. */
(() => {
  if (window.__ioraneCartInboxGuardInstalled) return;
  window.__ioraneCartInboxGuardInstalled = true;

  let observedDrawer = null;
  let drawerObserver = null;
  let mountObserver = null;

  const sync = () => {
    const drawer = document.querySelector('[data-drawer="cart"]');
    const open = !!drawer?.classList.contains('is-open');
    document.documentElement.classList.toggle('iorane-cart-active', open);
    if (drawer === observedDrawer) return;
    drawerObserver?.disconnect();
    observedDrawer = drawer;
    if (drawer) {
      drawerObserver = new MutationObserver(sync);
      drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  };

  const init = () => {
    sync();
    if (document.body && !mountObserver) {
      mountObserver = new MutationObserver(() => {
        if (!observedDrawer || !observedDrawer.isConnected) sync();
      });
      mountObserver.observe(document.body, { childList: true });
    }
    document.addEventListener('shopify:section:load', sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/**
 * manager-cart-drawer.js
 * Gerencia o drawer lateral do carrinho.
 *
 * Dependências (devem carregar antes):
 *   - window.eventBus       (manager-global-event-bus.js)
 *   - window.DrawerManager  (manager-drawer.js)
 *   - window.CartManager    (manager-cart.js)
 *
 * ─── Configuração em Liquid (dentro da section cart-drawer.liquid) ─────────
 *
 *   <script>
 *     window.cartDrawerConfig = { sectionId: '{{ section.id }}' };
 *   </script>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   data-cart-drawer-content   → wrapper do conteúdo substituído no refresh
 *   data-cart-drawer-loading   → indicador de loading (hidden por padrão)
 *
 *   data-cart-item             → wrapper de um item do carrinho
 *   data-remove-item           → botão remover (lê data-line-item-key)
 *   data-quantity-minus        → botão decrementar (lê data-line-item-key)
 *   data-quantity-plus         → botão incrementar (lê data-line-item-key)
 *   data-quantity-value        → span com quantidade atual
 *   data-line-item-key         → chave do line item no carrinho
 *
 *   data-cart-count            → exibe contagem total de itens
 *   data-cart-count-zero       → hidden quando carrinho vazio
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   await window.CartDrawerManager.refresh()
 *   window.CartDrawerManager.destroy()
 *
 * ─── Eventos DOM emitidos ─────────────────────────────────────────────────
 *
 *   'cart-drawer:refreshed' → conteúdo do drawer recém-atualizado no DOM
 *                             (usado pelo CartGiftManager para sincronizar brindes)
 */

class CartDrawerManager {
  constructor() {
    this._sectionId    = window.cartDrawerConfig?.sectionId;
    this._isRefreshing = false;
    this._refreshQueued = false;

    this._onCartAdded   = this._onCartAdded.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCartRemoved = this._onCartRemoved.bind(this);
    this._onContentClick = this._onContentClick.bind(this);

    this._bindEvents();
  }

  // ─── Bind de eventos ──────────────────────────────────────────────────────

  _bindEvents() {
    document.addEventListener('cart:added',   this._onCartAdded);
    document.addEventListener('cart:updated', this._onCartUpdated);
    document.addEventListener('cart:removed', this._onCartRemoved);
    document.addEventListener('click',        this._onContentClick);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.matches('[data-cart-coupon-input]')) return;
      event.preventDefault();
      event.target.closest('[data-cart-coupon]')?.querySelector('[data-cart-coupon-apply]')?.click();
    });
  }

  // ─── Resposta a eventos do CartManager ───────────────────────────────────

  async _onCartAdded(e) {
    await this.refresh();
    if (!e.detail?.action || e.detail.action === 'open_drawer') {
      window.DrawerManager?.open('cart');
    }
  }

  async _onCartUpdated() {
    await this.refresh();
  }

  async _onCartRemoved() {
    await this.refresh();
  }

  // ─── Event delegation para ações dentro do drawer ────────────────────────

  async _onContentClick(e) {
    // Navegacao discreta do carrossel de recomendacoes, sem scrollbar visivel.
    const upsellNav = e.target.closest('[data-cart-upsell-nav]');
    if (upsellNav) {
      e.preventDefault();
      const scroller = upsellNav.closest('.cartDrawer__upsells')?.querySelector('.cartDrawer__upsellsScroll');
      if (scroller) {
        const direction = upsellNav.dataset.cartUpsellNav === 'prev' ? -1 : 1;
        scroller.scrollBy({ left: direction * scroller.clientWidth * 0.86, behavior: 'smooth' });
      }
      return;
    }

    // Remover item
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineItemKey;
      if (key) window.CartManager?.remove(key, { button: removeBtn });
      return;
    }

    // Decrementar quantidade
    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      e.preventDefault();
      e.stopPropagation();
      const item = minusBtn.closest('[data-cart-item]');
      const key = minusBtn.dataset.lineItemKey || item?.dataset.lineItemKey;
      const qty = this._getQuantity(minusBtn);
      this._setQuantityError(item);
      if (!key) return;
      if (qty <= 1) {
        window.CartManager?.remove(key, { button: minusBtn });
      } else {
        window.CartManager?.update(key, qty - 1, { button: minusBtn });
      }
      return;
    }

    // Incrementar quantidade
    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      e.preventDefault();
      e.stopPropagation();
      const item = plusBtn.closest('[data-cart-item]');
      const key = plusBtn.dataset.lineItemKey || item?.dataset.lineItemKey;
      const qty = this._getQuantity(plusBtn);
      this._setQuantityError(item);
      if (key && Number.isFinite(qty)) {
        const result = await window.CartManager?.update(key, qty + 1, { button: plusBtn });
        if (result && !result.success) {
          const requestFailed = /network|failed to fetch|abort|timeout|http 5\d\d/i.test(result.error || '');
          const message = requestFailed
            ? 'Não foi possível aumentar a quantidade.'
            : 'Quantidade máxima disponível em estoque.';
          this._setQuantityError(item, message);
        }
      }
      return;
    }

    // Toggle Cupom
    const toggleSummary = e.target.closest('[data-cart-summary-toggle]');
    if (toggleSummary) {
      const expanded = toggleSummary.getAttribute('aria-expanded') === 'true';
      this._setSummaryExpanded(toggleSummary, !expanded);
      return;
    }

    // Cancelar Cupom
    const cancelCoupon = e.target.closest('[data-cart-coupon-cancel]');
    if (cancelCoupon) {
      const coupon = cancelCoupon.closest('[data-cart-coupon]');
      coupon?.querySelector('[data-cart-coupon-input]')?.setAttribute('value', '');
      const input = coupon?.querySelector('[data-cart-coupon-input]');
      if (input) input.value = '';
      const summaryToggle = cancelCoupon.closest('[data-cart-drawer-footer]')?.querySelector('[data-cart-summary-toggle]');
      if (summaryToggle) this._setSummaryExpanded(summaryToggle, false);
      coupon?.querySelector('[data-cart-coupon-error]')?.classList.add('hidden');
      return;
    }

    // Aplicar Cupom
    const applyCoupon = e.target.closest('[data-cart-coupon-apply]');
    if (applyCoupon) {
      const coupon = applyCoupon.closest('[data-cart-coupon]');
      const input = coupon?.querySelector('[data-cart-coupon-input]');
      const error = coupon?.querySelector('[data-cart-coupon-error]');
      if (input && input.value.trim()) {
        const result = await window.CartManager?.applyDiscount(input.value.trim(), { button: applyCoupon });
        if (!result?.success) {
          if (error) error.textContent = result.error || 'Cupom inválido.';
          error?.classList.remove('hidden');
        } else {
          error?.classList.add('hidden');
        }
      } else {
        if (error) error.textContent = 'Digite um cupom.';
        error?.classList.remove('hidden');
      }
      return;
    }

    // Remover Cupom
    const removeCoupon = e.target.closest('[data-cart-coupon-remove]');
    if (removeCoupon) {
      window.CartManager?.applyDiscount('', { button: removeCoupon });
      return;
    }

    // Abrir Mini PDP
    const miniPdpTrigger = e.target.closest('[data-mini-pdp-trigger]');
    if (miniPdpTrigger) {
      e.preventDefault();
      this._openMiniPdp(miniPdpTrigger.dataset.miniPdpTrigger, miniPdpTrigger);
      return;
    }

    // Cards de sugestão do minicart: qualquer link do card (imagem, título)
    // abre a Mini PDP em vez de levar o cliente para fora do carrinho.
    const upsellLink = e.target.closest('.cartDrawer__upsellItem a[href]');
    if (upsellLink) {
      e.preventDefault();
      this._openMiniPdp(upsellLink.getAttribute('href'), upsellLink);
      return;
    }
  }

  openMiniPdp(url, btn) {
    return this._openMiniPdp(url, btn);
  }

  _setSummaryExpanded(toggle, expanded) {
    const footer = toggle.closest('[data-cart-drawer-footer]');
    const coupon = footer?.querySelector('[data-cart-coupon]');
    if (!footer || !coupon) return;

    coupon.dataset.expanded = String(expanded);
    footer.dataset.summaryExpanded = String(expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    coupon.querySelector('[data-cart-coupon-content]')?.classList.toggle('hidden', !expanded);
    coupon.querySelector('[data-cart-coupon-form]')?.classList.remove('hidden');
    footer.querySelector('[data-cart-totals-breakdown]')?.classList.toggle('hidden', !expanded);
    coupon.querySelector('[data-cart-coupon-error]')?.classList.add('hidden');

    // Abre diretamente o campo, eliminando o clique intermediario em "Adicionar".
    if (expanded) {
      requestAnimationFrame(() => coupon.querySelector('[data-cart-coupon-input]')?.focus());
    }
  }

  async _openMiniPdp(url, btn) {
    if (!url || !btn) return;
    const modal = document.querySelector('#CartDrawerMiniPdp');
    if (!modal) return;

    const cartDrawer = modal.closest('[data-drawer="cart"]');
    const triggerInsideCart = Boolean(btn.closest('[data-drawer="cart"]'));
    const triggerInsideMiniPdp = Boolean(btn.closest('[data-mini-pdp]'));
    const externalOrigin = !triggerInsideCart;
    const mobileExternal = externalOrigin && window.matchMedia('(max-width: 989px)').matches;
    const previousScrollTop = triggerInsideMiniPdp
      ? (modal.querySelector('[data-mini-pdp-scroll]')?.scrollTop || 0)
      : 0;

    modal.dataset.miniPdpTransition = (triggerInsideMiniPdp || mobileExternal) ? 'none' : 'sheet';

    if (externalOrigin) {
      modal.dataset.miniPdpOrigin = 'external';
      if (cartDrawer) {
        cartDrawer.dataset.miniPdpExternal = 'true';
        if (mobileExternal) cartDrawer.dataset.drawerSide = 'bottom';
      }
    } else if (!triggerInsideMiniPdp) {
      modal.removeAttribute('data-mini-pdp-origin');
      cartDrawer?.removeAttribute('data-mini-pdp-external');
    }

    const useSpinner = btn.hasAttribute('data-mini-pdp-spinner');
    const originalText = useSpinner ? btn.innerHTML : null;
    if (useSpinner) {
      btn.innerHTML = '<span class="w-[1.6rem] h-[1.6rem] border-2 border-white border-t-transparent rounded-full animate-spin inline-block"></span>';
      btn.disabled = true;
    }

    modal.setAttribute('aria-busy', 'true');

    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', 'mini-pdp');
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error('Falha ao carregar o Mini PDP');
      const html = await res.text();

      const template = document.createElement('template');
      template.innerHTML = html;
      const firstImage = template.content.querySelector('[data-mini-pdp-slide] img');
      if (firstImage?.getAttribute('src')) {
        const preload = new Image();
        const ready = new Promise(resolve => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
          };
          preload.onload = finish;
          preload.onerror = finish;
          window.setTimeout(finish, 900);
        });
        preload.src = new URL(firstImage.getAttribute('src'), window.location.origin).href;
        await ready;
      }

      modal.innerHTML = html;
      modal.classList.remove('hidden');

      const scripts = modal.querySelectorAll('script');
      scripts.forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      const scroll = modal.querySelector('[data-mini-pdp-scroll]');
      if (scroll) {
        scroll.scrollTo({ top: triggerInsideMiniPdp ? previousScrollTop : 0, behavior: 'auto' });
      }

      if (externalOrigin && cartDrawer && window.DrawerManager && !window.DrawerManager.isOpen('cart')) {
        window.DrawerManager.open('cart', btn);
      }
    } catch (err) {
      console.error(err);
      if (!modal.querySelector('[data-mini-pdp]')) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
        if (cartDrawer) {
          cartDrawer.removeAttribute('data-mini-pdp-external');
          cartDrawer.dataset.drawerSide = 'right';
        }
      }
      alert('Erro ao carregar o produto.');
    } finally {
      modal.removeAttribute('aria-busy');
      if (useSpinner) {
        btn.innerHTML = originalText;
        btn.disabled = false;
      }
    }
  }

  /** Lê a quantidade atual de um item a partir do botão de controle. */
  _getQuantity(btn) {
    const buttonQuantity = parseInt(btn.dataset.quantityValue || '', 10);
    if (Number.isFinite(buttonQuantity)) return buttonQuantity;

    const item = btn.closest('[data-cart-item]');
    const valueEl = item?.querySelector('[data-quantity-value]');
    const rawQuantity = valueEl?.dataset.quantityValue || valueEl?.textContent?.trim() || '1';
    const quantity = parseInt(rawQuantity, 10);
    return Number.isFinite(quantity) ? quantity : 1;
  }

  _setQuantityError(item, message = '') {
    const errorEl = item?.querySelector('[data-cart-quantity-error]');
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.hidden = true;
    }
  }

  // ─── Section Rendering API ────────────────────────────────────────────────

  /**
   * Atualiza o conteúdo do drawer via Section Rendering API.
   * Previne chamadas concorrentes com a flag _isRefreshing — um refresh pedido
   * durante outro é enfileirado para rodar em seguida, nunca descartado.
   * Ao final de um refresh bem-sucedido emite 'cart-drawer:refreshed'.
   */
  async refresh() {
    if (!this._sectionId) return;
    if (this._isRefreshing) {
      this._refreshQueued = true;
      return;
    }

    this._isRefreshing = true;
    this._setLoading(true);
    let applied = false;

    try {
      const res = await fetch(`/?sections=${this._sectionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const sections = await res.json();
      const html     = sections[this._sectionId];
      if (!html) return;

      const doc        = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      const curContent = document.querySelector('[data-cart-drawer-content]');
      if (newContent && curContent) {
        curContent.innerHTML = newContent.innerHTML;
      }

      const newFooter = doc.querySelector('[data-cart-drawer-footer]');
      const curFooter = document.querySelector('[data-cart-drawer-footer]');
      if (newFooter && curFooter) {
        curFooter.innerHTML = newFooter.innerHTML;
      }

      this._updateCount(doc);
      applied = true;
    } catch (err) {
      console.error('[CartDrawerManager] Refresh failed:', err);
    } finally {
      this._isRefreshing = false;
      this._setLoading(false);
    }

    if (applied) {
      document.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));
    }

    if (this._refreshQueued) {
      this._refreshQueued = false;
      await this.refresh();
    }
  }

  /** Mostra ou oculta o indicador de loading. */
  _setLoading(show) {
    const el = document.querySelector('[data-cart-drawer-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  /** Atualiza todos os contadores de itens do carrinho na página. */
  _updateCount(doc) {
    const countEl = doc.querySelector('[data-cart-count]');
    if (!countEl) return;
    const count = countEl.textContent.trim();

    document.querySelectorAll('[data-cart-count]').forEach(el => {
      const valueEl = el.querySelector('[data-cart-count-value]');
      if (valueEl) valueEl.textContent = count;
      else el.textContent = count;
    });
    document.querySelectorAll('[data-cart-count-zero]').forEach(el => {
      el.hidden = count === '0';
    });
  }

  destroy() {
    document.removeEventListener('cart:added',   this._onCartAdded);
    document.removeEventListener('cart:updated', this._onCartUpdated);
    document.removeEventListener('cart:removed', this._onCartRemoved);
    document.removeEventListener('click',        this._onContentClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawerManager = new CartDrawerManager();
});
